// =====================================================
// 产品矩阵管理系统 - 类型定义
// 参考 Excalidraw 的类型系统设计
// =====================================================

// =====================================================
// 品牌类型 (Branded Types)
// 提供更强的类型安全，防止 ID 混淆
// =====================================================

/** 产品 ID 品牌类型 */
export type ProductId = string & { readonly _brand: 'ProductId' };

/** 型号 ID 品牌类型 */
export type ModelId = string & { readonly _brand: 'ModelId' };

/** 单元格索引品牌类型 */
export type CellIndex = number & { readonly _brand: 'CellIndex' };

/** 项目 ID 品牌类型 */
export type ProjectId = string & { readonly _brand: 'ProjectId' };

/** 模板 ID 品牌类型 */
export type TemplateId = string & { readonly _brand: 'TemplateId' };

// 类型断言辅助函数
export const asProductId = (id: string): ProductId => id as ProductId;
export const asModelId = (id: string): ModelId => id as ModelId;
export const asCellIndex = (index: number): CellIndex => index as CellIndex;
export const asProjectId = (id: string): ProjectId => id as ProjectId;
export const asTemplateId = (id: string): TemplateId => id as TemplateId;

// =====================================================
// 枚举类型
// =====================================================

/** 产品分类 */
export type ProductCategory = 'all_in_one' | 'product' | 'module';

/** 产品分类显示名称映射 */
export const ProductCategoryLabels: Record<ProductCategory, string> = {
  all_in_one: '一体机',
  product: '产品',
  module: '模组',
};

/** 布局类型 */
export type LayoutType = 'grid_2x2' | 'grid_3x3' | 'grid_4x4' | 'grid_9x9' | 'grid_custom';

/** 布局配置映射 */
export const LayoutConfig: Record<Exclude<LayoutType, 'grid_custom'>, { rows: number; cols: number; label: string }> = {
  grid_2x2: { rows: 2, cols: 2, label: '2×2' },
  grid_3x3: { rows: 3, cols: 3, label: '3×3' },
  grid_4x4: { rows: 4, cols: 4, label: '4×4' },
  grid_9x9: { rows: 9, cols: 9, label: '9×9' },
};

/** 风格类型 */
export type StyleType = 
  | 'standard'    // 标准布局 (图片上方，文字下方)
  | 'split'       // 左右分割
  | 'diagonal'    // 对角线动感
  | 'perspective' // 3D透视
  | 'asymmetric'  // 非对称重叠
  | 'magazine'    // 杂志风格
  | 'minimal'     // 极简留白
  | 'clean';      // 纯净模式 (无边框、透明背景)

/** 风格类型显示名称映射 */
export const StyleTypeLabels: Record<StyleType, string> = {
  standard: '标准布局',
  split: '左右分割',
  diagonal: '对角线动感',
  perspective: '3D透视',
  asymmetric: '非对称重叠',
  magazine: '杂志风格',
  minimal: '极简留白',
  clean: '纯净模式',
};

// =====================================================
// 产品相关类型
// =====================================================

/** 产品型号 (二级数据) */
export interface ProductModel {
  id: string;
  product_id: string;
  model_name: string;          // 如 "P0.6", "P0.9"
  pitch?: number;              // 具体间距值
  size_option?: string;        // 尺寸选项
  cabinet_size?: string;       // 箱体尺寸
  weight?: string;             // 重量
  power_consumption?: string;  // 功耗
  specs?: Record<string, any>; // 扩展规格
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/** 产品 (一级数据) */
export interface Product {
  id: string;
  name: string;                // 产品名称
  name_en?: string;            // 英文名称
  category: ProductCategory;   // 产品分类
  series_name?: string;        // 系列名称
  
  // 图片
  image_url?: string;          // 主图URL
  image_storage_key?: string;  // 存储key
  thumbnail_url?: string;      // 缩略图
  
  // 技术参数
  pitch_range?: string;        // 间距范围 "P0.6-P2.5"
  size_specs?: string;         // 尺寸规格
  process_type?: string;       // 工艺类型 COB/SMD/MIP
  resolution?: string;         // 分辨率
  brightness?: string;         // 亮度
  refresh_rate?: string;       // 刷新率
  
  // 描述性信息
  features?: string[];         // 特点描述
  slogan?: string;             // Slogan
  description?: string;        // 详细描述
  highlights?: Record<string, any>; // 亮点信息
  
  // 扩展参数
  specs?: Record<string, any>; // 其他技术规格
  
  // 状态
  sort_order: number;
  is_active: boolean;
  is_featured: boolean;
  
  // 关联数据
  models?: ProductModel[];     // 产品型号列表
  
  // 元数据
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

// =====================================================
// 画布相关类型
// =====================================================

/** 画布单元格数据 (增强版 - 支持版本控制) */
export interface CanvasCell {
  /** 格子索引 (0-based) */
  index: number;
  /** 行位置 */
  row: number;
  /** 列位置 */
  col: number;
  /** 产品ID */
  product_id?: string;
  /** 型号ID */
  model_id?: string;
  /** 自定义图片URL */
  custom_image?: string;
  /** 自定义文字 */
  custom_text?: {
    name?: string;
    series?: string;
    pitch?: string;
    features?: string[];
    slogan?: string;
  };
  /** 单格布局样式 (覆盖全局设置) */
  layout?: StyleType;
  
  // ===== 单元格合并字段 =====
  /** 行跨度 (默认1) */
  rowSpan?: number;
  /** 列跨度 (默认1) */
  colSpan?: number;
  /** 是否被隐藏（被合并到其他单元格） */
  hidden?: boolean;
  /** 被合并到哪个单元格的索引 */
  mergedInto?: number;
  
  // ===== 版本控制字段 (参考 Excalidraw) =====
  /** 版本号 - 每次修改递增 */
  version?: number;
  /** 最后更新时间戳 */
  updatedAt?: number;
}

/** 带版本控制的画布单元格 */
export interface VersionedCanvasCell extends CanvasCell {
  version: number;
  updatedAt: number;
}

/** 单元格更新类型 */
export type CellUpdate = Partial<Omit<CanvasCell, 'index' | 'row' | 'col'>>;

/** 判断单元格是否有内容 */
export function isCellFilled(cell: CanvasCell): boolean {
  return !!(cell.product_id || cell.custom_image || cell.custom_text?.name);
}

/** 创建空单元格 */
export function createEmptyCell(index: number, cols: number): CanvasCell {
  return {
    index,
    row: Math.floor(index / cols),
    col: index % cols,
    version: 1,
    updatedAt: Date.now(),
  };
}

/** 画布设置 */
export interface CanvasSettings {
  backgroundColor?: string;    // 背景色
  watermark?: {
    enabled: boolean;
    text?: string;
    image_url?: string;
    position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
    opacity?: number;
  };
  branding?: {
    enabled: boolean;
    logo_url?: string;
    company_name?: string;
    contact_info?: string;
    position?: 'top' | 'bottom';
  };
  padding?: number;            // 内边距
  gap?: number;                // 格子间距
}

/** 画布数据 (存储在 canvas_data 字段) */
export interface CanvasData {
  cells: CanvasCell[];
  settings?: CanvasSettings;
}

/** 画布模板 */
export interface CanvasTemplate {
  id: string;
  name: string;
  name_en?: string;
  description?: string;
  layout_type: LayoutType;
  style_type: StyleType;
  config?: {
    cellSize?: number;
    imageRatio?: string;
    padding?: number;
    gap?: number;
    backgroundColor?: string;
    textPosition?: string;
    [key: string]: any;
  };
  preview_image_url?: string;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

/** 画布项目 */
export interface CanvasProject {
  id: string;
  name: string;
  description?: string;
  template_id?: string;
  layout_type: LayoutType;
  style_type: StyleType;
  canvas_data: CanvasData;
  canvas_settings?: CanvasSettings;
  is_draft: boolean;
  is_public: boolean;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  
  // 关联数据
  template?: CanvasTemplate;
}

// =====================================================
// 导出相关类型
// =====================================================

/** 导出格式 */
export type ExportFormat = 'png' | 'jpg' | 'webp';

/** 导出记录 */
export interface CanvasExport {
  id: string;
  project_id: string;
  export_url: string;
  export_storage_key?: string;
  export_format: ExportFormat;
  resolution: number;
  total_width?: number;
  total_height?: number;
  quality: number;
  has_watermark: boolean;
  watermark_config?: Record<string, any>;
  file_size?: number;
  created_by?: string;
  created_at: string;
  expires_at?: string;
}

/** 导出选项 */
export interface ExportOptions {
  format: ExportFormat;
  resolution: number;          // 单格分辨率 (如 1080)
  quality: number;             // 图片质量 1-100
  includeWatermark: boolean;
  watermarkConfig?: CanvasSettings['watermark'];
}

// =====================================================
// UI 状态类型
// =====================================================

/** 拖拽项类型 */
export interface DragItem {
  type: 'product';
  product: Product;
  model?: ProductModel;
}

/** 产品筛选条件 */
export interface ProductFilter {
  category?: ProductCategory | 'all';
  search?: string;
  processType?: string;
  isFavorite?: boolean;
}

/** 画布操作历史项 (增强版) */
export interface CanvasHistoryItem {
  /** 唯一标识 */
  id: string;
  /** 时间戳 */
  timestamp: number;
  /** 操作类型 */
  action: CanvasAction;
  /** 操作的单元格索引 */
  cellIndex?: number;
  /** 操作描述 */
  description?: string;
  /** 之前的数据快照 */
  previousData?: CanvasData;
  /** 当前的数据快照 */
  currentData: CanvasData;
}

/** 画布操作类型 */
export type CanvasAction = 
  | 'add'           // 添加产品
  | 'remove'        // 移除产品
  | 'move'          // 移动/交换
  | 'update'        // 更新单元格
  | 'clear'         // 清空画布
  | 'layout_change' // 布局变更
  | 'style_change'  // 风格变更
  | 'settings_update' // 设置更新
  | 'batch';        // 批量操作

/** 捕获更新动作类型 (参考 Excalidraw) */
export const CaptureUpdateAction = {
  /** 立即可撤销 - 用于大多数用户操作 */
  IMMEDIATELY: 'IMMEDIATELY',
  /** 最终可撤销 - 用于连续操作（如拖拽中） */
  EVENTUALLY: 'EVENTUALLY',
  /** 永不记录 - 用于临时状态变更 */
  NEVER: 'NEVER',
} as const;

export type CaptureUpdateActionType = typeof CaptureUpdateAction[keyof typeof CaptureUpdateAction];

/** 历史快照 */
export interface HistorySnapshot {
  /** 快照 ID */
  id: string;
  /** 布局类型 */
  layoutType: LayoutType;
  /** 风格类型 */
  styleType: StyleType;
  /** 画布数据 */
  canvasData: CanvasData;
  /** 自定义行列数 */
  customRows?: number;
  customCols?: number;
  /** 坐标轴标签 */
  columnLabels?: string[];
  rowLabels?: string[];
  /** 创建时间 */
  timestamp: number;
}

// =====================================================
// API 响应类型
// =====================================================

export interface ApiResponse<T> {
  data: T | null;
  error: Error | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}
